import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Users, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [services, setServices] = useState([]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data.slice(0, 3));
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  return (
    <div className="flex-1" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-primary-dark text-white py-20 md:py-32 overflow-hidden">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)'}}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="inline-block px-4 py-2 bg-primary/20 rounded-full border border-primary/30">
                <span className="text-primary text-sm font-medium">Community-Driven • Data-Driven • Impact-Driven</span>
              </div>
              <div className="inline-block px-4 py-2 bg-accent-sand/20 rounded-full border border-accent-sand/30">
                <span className="text-accent-sand text-sm font-semibold">A Priority 1 Navajo Owned Business</span>
              </div>
            </div>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight font-heading"
              data-testid="hero-heading"
            >
              Strengthening Health Outcomes for Tribal & Rural Communities
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-6 leading-relaxed">
              A comprehensive center providing population-health, community-health, and public-health solutions designed to improve program performance and build data-driven decision-making capacity.
            </p>
            <div className="inline-block px-4 py-2 bg-accent-sage/20 rounded-lg border border-accent-sage/30 mb-8">
              <span className="text-accent-sage text-sm font-medium">Our services qualify for Navajo Nation Junk Food Tax wellness funding</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/services">
                <Button 
                  size="lg" 
                  className="bg-primary hover:bg-primary/90 text-white font-semibold shadow-lg"
                  data-testid="hero-services-button"
                >
                  Explore Our Services
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-primary text-primary bg-white hover:bg-primary hover:text-white font-semibold"
                  data-testid="hero-contact-button"
                >
                  Get In Touch
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-md hover:shadow-xl transition-all duration-300 bg-white hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-accent-sage/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 border-accent-sage/30">
                  <Target className="text-accent-sage" size={32} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-neutral-charcoal font-heading">Mission-Focused</h3>
                <p className="text-neutral-gray text-sm leading-relaxed">
                  Dedicated to strengthening health outcomes through community-centered, data-driven approaches.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md hover:shadow-xl transition-all duration-300 bg-white hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 border-primary/30">
                  <Users className="text-primary" size={32} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-neutral-charcoal font-heading">Community-Centered</h3>
                <p className="text-neutral-gray text-sm leading-relaxed">
                  Culturally-grounded solutions tailored for tribal communities and rural healthcare organizations.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md hover:shadow-xl transition-all duration-300 bg-white hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-accent-clay/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 border-accent-clay/30">
                  <TrendingUp className="text-accent-clay" size={32} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-neutral-charcoal font-heading">Data-Driven</h3>
                <p className="text-neutral-gray text-sm leading-relaxed">
                  Evidence-based analytics and insights to guide strategic health interventions and program improvements.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-1 bg-primary/10 rounded-full mb-4">
              <span className="text-primary text-sm font-semibold">What We Offer</span>
            </div>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-neutral-charcoal mb-4 font-heading"
              data-testid="services-heading"
            >
              Our Core Services
            </h2>
            <p className="text-lg text-neutral-gray max-w-2xl mx-auto">
              Comprehensive solutions designed to strengthen health outcomes and build capacity
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12" data-testid="services-grid">
            {services.map((service, index) => {
              const colors = ['border-primary bg-primary/5', 'border-accent-sage bg-accent-sage/5', 'border-accent-sand bg-accent-sand/5'];
              const iconColors = ['text-primary', 'text-accent-sage', 'text-accent-sand'];
              return (
                <Card key={service.id} className={`hover:shadow-xl transition-all duration-300 border-l-4 ${colors[index % 3]} hover:-translate-y-1`}>
                  <CardContent className="p-6">
                    <h3 className={`text-xl font-bold mb-3 ${iconColors[index % 3]} font-heading`}>
                      {service.title}
                    </h3>
                    <p className="text-xs font-medium text-neutral-gray mb-3 uppercase tracking-wide">{service.category}</p>
                    <p className="text-neutral-charcoal mb-4 text-sm leading-relaxed">{service.description}</p>
                    <Link to="/services">
                      <Button variant="link" className={`${iconColors[index % 3]} p-0 font-semibold hover:underline`} data-testid={`service-learn-more-${service.id}`}>
                        Learn More <ArrowRight className="ml-1" size={16} />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button size="lg" className="bg-primary hover:bg-primary-dark text-white font-semibold shadow-lg" data-testid="view-all-services-button">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.2) 35px, rgba(255,255,255,.2) 70px)'}}></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 font-heading">
            Ready to Strengthen Your Community&apos;s Health?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Let&apos;s work together to improve health outcomes and build data-driven capacity for your organization.
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-white text-primary hover:bg-gray-100 font-semibold shadow-lg"
              data-testid="cta-contact-button"
            >
              Schedule a Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
